import { FC } from 'react';
import { SkeletonItem } from 'stubben-edge-unified-frontend-react-components';

type AppShellSkeletonProps = {
  variant?: 'full' | 'content';
};

const SIDEBAR_NAV_ITEMS = 8;
const HEADER_HEIGHT_PX = 90;

const AppShellSkeleton: FC<AppShellSkeletonProps> = ({ variant = 'full' }) => {
  return (
    <div
      className="min-h-screen bg-gray-50"
      aria-busy="true"
      aria-label="Loading"
    >
      {variant === 'full' && (
        <>
          <div
            className="shrink-0"
            style={{ height: `${HEADER_HEIGHT_PX}px` }}
            data-testid="app-shell-skeleton-header"
          >
            <SkeletonItem className="h-full w-full rounded-none" />
          </div>
          <div
            className="flex overflow-hidden"
            style={{ height: `calc(100vh - ${HEADER_HEIGHT_PX}px)` }}
          >
            <AppShellSidebarSkeleton />
            <AppShellContentSkeleton />
          </div>
        </>
      )}
      {variant === 'content' && <AppShellContentSkeleton />}
    </div>
  );
};

function AppShellSidebarSkeleton() {
  return (
    <aside
      className="z-0 hidden h-full w-16 shrink-0 flex-col items-center gap-4 overflow-y-auto border-r border-gray-200 bg-white p-2 lg:flex"
      aria-hidden
      data-testid="app-shell-skeleton-sidebar"
    >
      <SkeletonItem className="h-8 w-8 rounded-full" />
      <div className="flex w-full flex-col items-center gap-2">
        {Array.from({ length: SIDEBAR_NAV_ITEMS }, (_, i) => (
          <SkeletonItem key={i} className="h-8 w-8 shrink-0 rounded-md" />
        ))}
      </div>
      <div className="mt-auto flex flex-col items-center gap-2 border-t border-gray-200 pt-4">
        <SkeletonItem className="h-8 w-8 shrink-0 rounded-md" />
        <SkeletonItem className="h-8 w-8 shrink-0 rounded-md" />
      </div>
    </aside>
  );
}

function AppShellContentSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-6 p-6 xl:px-9">
      <div className="flex items-center justify-between gap-4">
        <SkeletonItem className="h-8 w-48" />
        <div className="flex gap-2">
          <SkeletonItem className="h-10 w-28" />
          <SkeletonItem className="h-10 w-32" />
        </div>
      </div>
      <SkeletonItem className="h-10 w-full max-w-xl" />
      <SkeletonItem className="h-64 w-full" />
      <SkeletonItem className="h-48 w-full" />
    </div>
  );
}

export default AppShellSkeleton;
